import type { ComponentPropsWithoutRef } from "react";
import type { Control } from "react-hook-form";
import { t } from "ttag";

import { GENDER } from "@/shared/models";
import type { UserAttributes } from "@/shared/models";
import { Form, GenderIcon, Select } from "@/shared/ui";

interface FieldValue {
  gender: UserAttributes["gender"];
}

type GenderControl<T> = T extends FieldValue ? Control<T> : never;

type GenderFieldProps<T extends FieldValue> = ComponentPropsWithoutRef<
  typeof Form.Item
> & {
  control: GenderControl<T>;
  onGenderChange?: (gender: UserAttributes["gender"]) => void;
  compactMessage?: boolean;
};

export const GenderField = <T extends FieldValue>({
  control,
  onGenderChange,
  compactMessage = false,
  ...props
}: GenderFieldProps<T>) => {
  const GENDER_OPTIONS = [
    { value: GENDER.MALE, label: t`Male` },
    { value: GENDER.FEMALE, label: t`Female` },
  ] as const;

  return (
    <Form.Field
      control={control}
      name="gender"
      render={({ field: { value, onChange, ...field } }) => (
        <Form.Item {...props}>
          <Form.Label aria-required>{t`Sex assigned at birth`}</Form.Label>
          <Form.Control>
            <Select
              value={value ?? ""}
              options={GENDER_OPTIONS}
              onChange={(value) => {
                onChange(value);
                onGenderChange?.(value);
              }}
              leftIcon={<GenderIcon />}
              {...field}
            />
          </Form.Control>
          <Form.Message compact={compactMessage} />
        </Form.Item>
      )}
    />
  );
};
